import { useState } from "react";
import { oaMode, takeDay, type OaMode, type PrepEvent } from "../../utils/prepPlanner";
import { addDays, daysBetween } from "../../utils/prepSkills";
import { cn } from "../../lib/utils";
import { DebriefDialog, type DebriefProps } from "./PrepDebrief";
import type { Crowd } from "../../utils/companyDebriefs";
import { topicName } from "../../data/prep/topics";

// One OA the plan is working toward, above the week calendar: when it's due,
// the stages of its prep with today's lit up, and "I'll prep myself" to stop
// planning for it. Once it's over,
// it asks for the debrief instead, and goes away once that's written.

export const OA_COLOR = { block: "bg-[#F6E3D3] text-[#7A3E1D]", dot: "#C9773F" };

const STAGES: { mode: OaMode; label: string }[] = [
  { mode: "ramp", label: "Warm up" },
  { mode: "focus", label: "Fix gaps" },
  { mode: "light", label: "Sharpen" },
  { mode: "oa", label: "OA" },
  { mode: "after", label: "Debrief" },
];

/** "Fri, Oct 23" */
const dayLabel = (day: string) =>
  new Date(`${day}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

export default function PrepOaCard({
  event,
  today,
  onSelfPrep,
  debrief,
  crowd,
  hideTopics,
}: {
  event: PrepEvent;
  today: string;
  onSelfPrep: () => void;
  debrief: DebriefProps;
  /** What earlier candidates here said */
  crowd?: Crowd;
  /** Don't name the topics they got stuck on */
  hideTopics?: boolean;
}) {
  const [allSaid, setAllSaid] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [debriefing, setDebriefing] = useState(false);
  const take = takeDay(event, today);
  const left = daysBetween(today, take);
  const mode = oaMode(left);

  // Over: just the debrief, until it's written
  if (left < 0) {
    if (event.debriefed) return null;
    return (
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-2xl border border-melt-line bg-melt-paper p-4">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-[15px] font-semibold">
            <i className="h-2.5 w-2.5 shrink-0 rounded-[3px]" style={{ background: OA_COLOR.dot }} aria-hidden="true" />
            How did the {event.company} OA go?
          </p>
          <p className="mt-0.5 font-plex text-[11px] text-melt-text3">
            Taken {dayLabel(take)} · three taps, and your plan works on where you got stuck
          </p>
        </div>
        <DebriefDialog
          open={debriefing}
          onOpenChange={setDebriefing}
          debrief={debrief}
          trigger={
            <button
              type="button"
              className="h-9 shrink-0 rounded-full bg-melt-caramel px-4 text-[13.5px] font-medium text-white transition-colors hover:bg-melt-press"
            >
              Write the debrief
            </button>
          }
        />
      </div>
    );
  }

  const when =
    left > 1 ? `in ${left} days` : left === 1 ? "tomorrow" : left === 0 ? "today" : `${-left} ${left === -1 ? "day" : "days"} ago`;
  const meta = event.dueKnown
    ? `Due ${dayLabel(event.due)}${take !== event.due ? ` · take it ${dayLabel(take)}, a day early` : ""}`
    : `No date given · aiming for ${dayLabel(event.due)}`;
  // Where the focus stage starts and the OA is taken, for the strip's dates
  const startsOn: Partial<Record<OaMode, string>> = {
    focus: addDays(take, -6),
    light: addDays(take, -2),
    oa: take,
    after: addDays(take, 1),
  };

  return (
    <div className="rounded-2xl border border-melt-line bg-melt-paper p-4">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-[15px] font-semibold">
            <i className="h-2.5 w-2.5 shrink-0 rounded-[3px]" style={{ background: OA_COLOR.dot }} aria-hidden="true" />
            {event.company} OA {when}
          </p>
          <p className="mt-0.5 font-plex text-[11px] text-melt-text3">{meta}</p>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => (confirming ? onSelfPrep() : setConfirming(true))}
            onBlur={() => setConfirming(false)}
            className={cn(
              "h-8 rounded-[9px] px-2.5 text-[12.5px] font-medium transition-colors",
              confirming ? "bg-[#A5492E]/[.08] text-[#A5492E]" : "text-melt-text3 hover:bg-melt-text1/[.06] hover:text-melt-text1"
            )}
          >
            {confirming ? "Stop planning for it?" : "I’ll prep myself"}
          </button>
        </div>
      </div>

      {crowd && (
        <div className="mt-3 rounded-xl bg-white/70 px-3 py-2.5">
          <p className="text-[12.5px] text-melt-text2">
            <span className="font-semibold text-melt-text1">
              {crowd.people === 1 ? "Someone" : `${crowd.people} people`} took this OA
              {crowd.sameRole ? " for this role" : ""}.
            </span>{" "}
            {!crowd.topics.length
              ? "Nobody got stuck on a topic."
              : hideTopics
                ? "Your plan starts where they got stuck."
                : `Got stuck on ${crowd.topics.slice(0, 3).map(topicName).join(", ")}, so your plan starts there.`}
          </p>
          {crowd.said.length > 0 && (
            <ul className="mt-1.5 flex flex-col gap-1">
              {(allSaid ? crowd.said : crowd.said.slice(0, 3)).map((text, i) => (
                <li key={i} className="border-l-2 border-[#E9C9AE] pl-2 text-[12.5px] leading-[1.45] text-melt-text1">
                  {text}
                </li>
              ))}
              {crowd.said.length > 3 && (
                <li>
                  <button
                    type="button"
                    onClick={() => setAllSaid(!allSaid)}
                    className="text-[12px] font-medium text-melt-text2 underline decoration-melt-text3/40 underline-offset-2 hover:text-melt-text1"
                  >
                    {allSaid ? "Show less" : `Show all ${crowd.said.length}`}
                  </button>
                </li>
              )}
            </ul>
          )}
        </div>
      )}

      <ol className="mt-3 grid grid-cols-5 gap-1" aria-label="Prep stages">
        {STAGES.map((stage, i) => {
          const now = stage.mode === mode;
          const past = STAGES.findIndex((s) => s.mode === mode) > i;
          const from = startsOn[stage.mode];
          return (
            <li key={stage.mode} aria-current={now ? "step" : undefined}>
              <div
                className={cn("h-1.5 rounded-full", past || now ? "" : "bg-melt-track")}
                style={past || now ? { background: OA_COLOR.dot, opacity: now ? 1 : 0.45 } : undefined}
              />
              <p className={cn("mt-1.5 truncate text-[12px]", now ? "font-semibold text-melt-text1" : "text-melt-text3")}>{stage.label}</p>
              {from && <p className="truncate font-plex text-[10.5px] text-melt-text3">{dayLabel(from).replace(/^\w+, /, "")}</p>}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
